// Genre theming: the app's chrome in a Fantasy (original purple), Sci-Fi (cyan), Horror (blood
// and bone), Post-Apocalyptic (rust and toxic green), Pirate (brass and navy), Urban Fantasy
// (neon magenta on a blue-black night), Samurai (vermilion lacquer, sumi ink and washi), Cyberpunk
// (acid yellow and hazard red on chrome) or Primal (cave-painting ochres on charcoal) palette.
//
// Most colors are CSS variables (src/theme/genre.css): every brand purple was replaced by
// var(--th-<hex>), which :root[data-genre="scifi"], [data-genre="horror"],
// [data-genre="postapoc"], [data-genre="pirate"], [data-genre="urban"], [data-genre="japan"], [data-genre="cyberpunk"] and [data-genre="primal"] remap. ThemeContext sets data-genre on
// <html>, so switching repaints without a reload.
//
// Two things cannot take a CSS variable, and use the helpers here instead:
//  - an alpha suffix glued onto a color (`${color}55`) -> withAlpha(color, '55')
//  - anything that parses the color itself: MUI's createTheme/alpha(), Konva canvas
//    props, three.js -> themeHex('#6C4F9B', genre)
//
// Colors that are data (saved token colors, game-system accents, dice) are deliberately
// left as plain hex - a CSS variable saved to the database would break other readers.

export const GENRES = [
    {value: 'fantasy', label: 'Fantasy'},
    {value: 'scifi', label: 'Sci-Fi'},
    {value: 'horror', label: 'Horror'},
    {value: 'postapoc', label: 'Post-Apocalyptic'},
    {value: 'pirate', label: 'Pirate'},
    {value: 'urban', label: 'Urban Fantasy'},
    {value: 'japan', label: 'Samurai'},
    {value: 'cyberpunk', label: 'Cyberpunk'},
    {value: 'primal', label: 'Primal'},
];
export const DEFAULT_GENRE = 'fantasy';
export const normalizeGenre = (value) => (GENRES.some(g => g.value === value) ? value : DEFAULT_GENRE);

/**
 * A settings payload minus the genre (and the tavern's one-click setting, for the same reason).
 * Global Settings is the one place that changes them; the tabletop and Settings page post the
 * whole settings object they loaded earlier, which would otherwise write a stale value back over
 * a change made since. The server leaves the stored values alone when the fields are absent
 * (SettingService).
 */
export const withoutGenre = (settings) => {
    if (!settings || typeof settings !== 'object') return settings;
    const {uiGenre, tavernOneClickNav, ...rest} = settings;
    return rest;
};

// Fantasy hex (no #) -> sci-fi hex. Mirrors the [data-genre="scifi"] block in theme/genre.css;
// change a color in both places.
export const SCIFI_PALETTE = {
    '05040A': '04090A',
    '080612': '061112',
    '0A0812': '081112',
    '0D0A14': '0A1214',
    '0D0B1A': '0B181A',
    '0F0C16': '0C1416',
    '0F0D18': '0D1718',
    '120C18': '0C1518',
    '12101E': '101C1E',
    '13101E': '101C1E',
    '140E1C': '0E191C',
    '161220': '121E20',
    '1A1225': '122125',
    '1A1229': '122529',
    '1E1530': '132D32',
    '1E1628': '14252A',
    '1E1630': '142D32',
    '1E1A2A': '18292C',
    '24123E': '0F3741',
    '251A44': '174047',
    '2A1E40': '1B3C43',
    '2A2038': '1E343A',
    '2C2433': '223035',
    '2E1F52': '1B4C56',
    '2E2050': '1D4B53',
    '3A2560': '215864',
    '3A2F4D': '2C4950',
    '3A3050': '2D4D53',
    '3D2A66': '265F6A',
    '3D2B5C': '275560',
    '3D2D5C': '295660',
    '4A3070': '2B6675',
    '4A3470': '2F6875',
    '4A3570': '306875',
    '4A3A5E': '365962',
    '4A3A6A': '36656E',
    '4A3B66': '37616A',
    '4E3870': '336875',
    '5530A8': '2999AF',
    '563E7C': '397381',
    '5A3D8A': '377F90',
    '5A4080': '3B7685',
    '5B3F8A': '397F90',
    '5C3D9C': '3690A3',
    '6432C8': '29B3D1',
    '6444B4': '3CA8BC',
    '6B47A8': '409AAF',
    '6C4F9B': '4890A2',
    '7A5BAE': '54A3B5',
    '7B5FB5': '58ABBC',
    '7C5FAB': '58A1B2',
    '7D5BAD': '54A0B4',
    '7D5CB0': '55A4B7',
    '7D5CB3': '55A7BA',
    '7D5CB8': '55ABBF',
    '7D5DB5': '56A9BC',
    '8060A8': '599CAF',
    '8261B8': '5AACBF',
    '8B6FBF': '69B4C5',
    '8E6CC8': '66BBCE',
    '8F2DAF': '2588B7',
    '9070C0': '6AB4C6',
    '9890BC': '8BBAC1',
    '9A7BDE': '75D1E4',
    '9B7FD4': '79C9DA',
    '9C6FD6': '69C4DC',
    '9E8AB5': '85AEBA',
    'A07DD0': '77C2D6',
    'A098C4': '94C2C8',
    'A89BC2': '97BEC6',
    'A8A0C8': '9CC6CC',
    'A8A0CC': '9CCAD0',
    'B0A8CC': 'A4CAD0',
    'B57EDC': '78C6E2',
    'B8A8C8': 'A4C2CC',
    'B8B0C8': 'ADC6CB',
    'B8B0D4': 'ADD2D7',
    'BCB4D6': 'B1D3D9',
    'BFA6E4': 'A2DAE8',
    'C0B8DC': 'B5D9DF',
    'C8B4FF': 'B4F4FF',
    'C9A0F0': '9CDFF4',
    'D2AAFF': 'AAECFF',
    'D4C5E8': 'C3E2EA',
    'D8C7F0': 'C7E8F0',
    'DCC4F2': 'C4E7F2',
    'E4E0EC': 'E0EAEC',
    'E8D5FF': 'D5F6FF',
    'E8E0F0': 'E0ECF0',
    'E996FF': '96DCFF',
    'F0E8FF': 'E8FBFF',
    'F5F0FF': 'F0FCFF',};

// Fantasy hex (no #) -> horror hex. Mirrors the [data-genre="horror"] block in theme/genre.css.
export const HORROR_PALETTE = {
    '05040A': '090506',
    '080612': '0F0909',
    '0A0812': '100A0B',
    '0D0A14': '120C0D',
    '0D0B1A': '170E0F',
    '0F0C16': '140E0F',
    '0F0D18': '160F10',
    '120C18': '150F0F',
    '12101E': '1B1314',
    '13101E': '1B1314',
    '140E1C': '191112',
    '161220': '1D1516',
    '1A1225': '211617',
    '1A1229': '241718',
    '1E1530': '2A1B1C',
    '1E1628': '241A1B',
    '1E1630': '2A1C1D',
    '1E1A2A': '261E1E',
    '24123E': '341C1D',
    '251A44': '3B2325',
    '2A1E40': '382627',
    '2A2038': '332526',
    '2C2433': '302728',
    '2E1F52': '472A2C',
    '2E2050': '452B2C',
    '3A2560': '533234',
    '3A2F4D': '463637',
    '3A3050': '493738',
    '3D2A66': '6B171D',
    '3D2B5C': '513637',
    '3D2D5C': '513839',
    '4A3070': '751B21',
    '4A3470': '742026',
    '4A3570': '732127',
    '4A3A5E': '5E2B2F',
    '4A3A6A': '6B282D',
    '4A3B66': '662A2E',
    '4E3870': '73252A',
    '5530A8': 'A71B25',
    '563E7C': '7F282E',
    '5A3D8A': '8F242B',
    '5A4080': '832A30',
    '5B3F8A': '8F262D',
    '5C3D9C': 'A41F28',
    '6432C8': 'C2202A',
    '6444B4': 'BE212C',
    '6B47A8': 'AF2831',
    '6C4F9B': '9E343B',
    '7A5BAE': 'B63941',
    '7B5FB5': 'C3353F',
    '7C5FAB': 'B13E46',
    '7D5BAD': 'B43942',
    '7D5CB0': 'B93840',
    '7D5CB3': 'BF353E',
    '7D5CB8': 'C8313B',
    '7D5DB5': 'C3343E',
    '8060A8': '9A846E',
    '8261B8': '93484D',
    '8B6FBF': 'A24F54',
    '8E6CC8': 'AE4950',
    '8F2DAF': 'AA1C25',
    '9070C0': 'A44F55',
    '9890BC': 'B3A699',
    '9A7BDE': 'CB5159',
    '9B7FD4': 'BF575E',
    '9C6FD6': 'C2464E',
    '9E8AB5': 'ACA093',
    'A07DD0': 'BA565C',
    'A098C4': 'BBAEA1',
    'A89BC2': 'BAAEA3',
    'A8A0C8': 'C0B4A8',
    'A8A0CC': 'B37B7F',
    'B0A8CC': 'C5BAAF',
    'B57EDC': 'C8545C',
    'B8A8C8': 'C2B8AE',
    'B8B0C8': 'C3BCB5',
    'B8B0D4': 'CDC2B7',
    'BCB4D6': 'CFC5BB',
    'BFA6E4': 'D17C81',
    'C0B8DC': 'D0CBC4',
    'C8B4FF': 'E7DCCC',
    'C9A0F0': 'D6CBBA',
    'D2AAFF': 'E3D7C6',
    'D4C5E8': 'DDD8D0',
    'D8C7F0': 'E3DDD4',
    'DCC4F2': 'E3DDD3',
    'E4E0EC': 'EAE6E2',
    'E8D5FF': 'F1EBE3',
    'E8E0F0': 'EBE9E5',
    'E996FF': 'DDCEB8',
    'F0E8FF': 'F8F4EF',
    'F5F0FF': 'FAF8F5',
};

// Fantasy hex (no #) -> post-apocalyptic hex. Mirrors the [data-genre="postapoc"] block in theme/genre.css.
export const POSTAPOC_PALETTE = {
    '05040A': '080706',
    '080612': '0F0C09',
    '0A0812': '0F0D0B',
    '0D0A14': '110F0D',
    '0D0B1A': '16120F',
    '0F0C16': '13110F',
    '0F0D18': '151210',
    '120C18': '15120F',
    '12101E': '1A1714',
    '13101E': '1A1714',
    '140E1C': '181512',
    '161220': '1C1916',
    '1A1225': '201B17',
    '1A1229': '231D18',
    '1E1530': '29221C',
    '1E1628': '231F1B',
    '1E1630': '29231D',
    '1E1A2A': '26221E',
    '24123E': '32271E',
    '251A44': '382E26',
    '2A1E40': '372E27',
    '2A2038': '312C27',
    '2C2433': '2F2B28',
    '2E1F52': '44382D',
    '2E2050': '43372D',
    '3A2560': '504235',
    '3A2F4D': '453E37',
    '3A3050': '474039',
    '3D2A66': '723517',
    '3D2B5C': '4F4338',
    '3D2D5C': '4F443A',
    '4A3070': '7D3C1B',
    '4A3470': '7B3F20',
    '4A3570': '7B3F22',
    '4A3A5E': '643F2D',
    '4A3A6A': '724229',
    '4A3B66': '6D422C',
    '4E3870': '7A4225',
    '5530A8': 'AE4F1F',
    '563E7C': '874929',
    '5A3D8A': '994B24',
    '5A4080': '8C4B2B',
    '5B3F8A': '984C26',
    '5C3D9C': 'AF4F1F',
    '6432C8': 'CA5B24',
    '6444B4': 'C85A23',
    '6B47A8': 'BB5928',
    '6C4F9B': 'A95C35',
    '7A5BAE': 'C2673A',
    '7B5FB5': 'CC6B3A',
    '7C5FAB': 'BD693F',
    '7D5BAD': 'C0673B',
    '7D5CB0': 'C66839',
    '7D5CB3': 'CA6837',
    '7D5CB8': 'D16935',
    '7D5DB5': 'CD6938',
    '8060A8': '9D8B6B',
    '8261B8': '98AA50',
    '8B6FBF': 'A2B35C',
    '8E6CC8': 'A9BD58',
    '8F2DAF': 'B2501F',
    '9070C0': 'A3B45D',
    '9890BC': 'B5AA97',
    '9A7BDE': 'BFD565',
    '9B7FD4': 'B7CA6B',
    '9C6FD6': 'B5CC5A',
    '9E8AB5': 'AFA490',
    'A07DD0': 'B3C569',
    'A098C4': 'BDB29F',
    'A89BC2': 'BCB2A1',
    'A8A0C8': 'C2B8A6',
    'A8A0CC': 'B6C08D',
    'B0A8CC': 'C7BDAD',
    'B57EDC': 'BDD369',
    'B8A8C8': 'C3BBAD',
    'B8B0C8': 'C4BEB4',
    'B8B0D4': 'CFC5B5',
    'BCB4D6': 'D1C8B9',
    'BFA6E4': 'CCDB90',
    'C0B8DC': 'D2CDC2',
    'C8B4FF': 'EADFC9',
    'C9A0F0': 'DACEB6',
    'D2AAFF': 'E8DBC1',
    'D4C5E8': 'DED9CF',
    'D8C7F0': 'E5DFD2',
    'DCC4F2': 'E5DED1',
    'E4E0EC': 'EAE7E2',
    'E8D5FF': 'F3EDE1',
    'E8E0F0': 'ECE9E4',
    'E996FF': 'E2D2B3',
    'F0E8FF': 'F9F5EE',
    'F5F0FF': 'FBF9F4',
};

// Fantasy hex (no #) -> pirate hex. Mirrors the [data-genre="pirate"] block in theme/genre.css.
export const PIRATE_PALETTE = {
    '05040A': '03070B',
    '080612': '060C14',
    '0A0812': '070D14',
    '0D0A14': '090F16',
    '0D0B1A': '0A121D',
    '0F0C16': '0B1118',
    '0F0D18': '0C121B',
    '120C18': '0B121B',
    '12101E': '0F1721',
    '13101E': '0F1721',
    '140E1C': '0D151F',
    '161220': '111923',
    '1A1225': '101B29',
    '1A1229': '101D2E',
    '1E1530': '132236',
    '1E1628': '151F2C',
    '1E1630': '142236',
    '1E1A2A': '19222E',
    '24123E': '132741',
    '251A44': '162E4C',
    '2A1E40': '1B2E48',
    '2A2038': '1E2C3E',
    '2C2433': '242C38',
    '2E1F52': '1B375C',
    '2E2050': '1B375A',
    '3A2560': '1F416C',
    '3A2F4D': '2D3E55',
    '3A3050': '2E4058',
    '3D2A66': '694C19',
    '3D2B5C': '274367',
    '3D2D5C': '294467',
    '4A3070': '74541C',
    '4A3470': '75551E',
    '4A3570': '76561F',
    '4A3A5E': '3B4B5D',
    '4A3A6A': '405164',
    '4A3B66': '3F4F62',
    '4E3870': '775721',
    '5530A8': 'A6731D',
    '563E7C': '836024',
    '5A3D8A': '8F6824',
    '5A4080': '886425',
    '5B3F8A': '906925',
    '5C3D9C': '9F7224',
    '6432C8': 'C3861E',
    '6444B4': 'B78328',
    '6B47A8': 'AE7D2A',
    '6C4F9B': 'A5792E',
    '7A5BAE': 'BC8933',
    '7B5FB5': 'C69033',
    '7C5FAB': 'BA8935',
    '7D5BAD': 'BB8933',
    '7D5CB0': 'BE8B33',
    '7D5CB3': 'C28D32',
    '7D5CB8': 'C79031',
    '7D5DB5': 'C58F32',
    '8060A8': '69829F',
    '8261B8': 'CA9333',
    '8B6FBF': 'E5B949',
    '8E6CC8': 'E6BB4E',
    '8F2DAF': 'B8323B',
    '9070C0': 'E5B94B',
    '9890BC': 'B8AC94',
    '9A7BDE': 'EAC76F',
    '9B7FD4': 'EAC569',
    '9C6FD6': 'E8C15D',
    '9E8AB5': 'B3A68C',
    'A07DD0': 'E9C364',
    'A098C4': 'BEB39E',
    'A89BC2': 'BFB49E',
    'A8A0C8': 'C3B9A5',
    'A8A0CC': 'C5BBA7',
    'B0A8CC': 'C8BFAC',
    'B57EDC': 'EBC870',
    'B8A8C8': 'C6BDAA',
    'B8B0C8': 'C9C0AF',
    'B8B0D4': 'CDC6B7',
    'BCB4D6': 'CFC9BB',
    'BFA6E4': 'D8CCB2',
    'C0B8DC': 'D5CEBF',
    'C8B4FF': 'EEE2C5',
    'C9A0F0': 'E0D2B0',
    'D2AAFF': 'ECDEBD',
    'D4C5E8': 'E1DBCC',
    'D8C7F0': 'E8E0CF',
    'DCC4F2': 'E9E1CD',
    'E4E0EC': 'EAE7E2',
    'E8D5FF': 'F6EFDE',
    'E8E0F0': 'EDEAE3',
    'E996FF': 'F1AE9D',
    'F0E8FF': 'FAF6ED',
    'F5F0FF': 'FCF9F3',
};

// Fantasy hex (no #) -> urban fantasy hex. Mirrors the [data-genre="urban"] block in theme/genre.css.
export const URBAN_PALETTE = {
    '05040A': '050609',
    '080612': '070911',
    '0A0812': '090B11',
    '0D0A14': '0B0D13',
    '0D0B1A': '0C0F18',
    '0F0C16': '0D0F15',
    '0F0D18': '0E1017',
    '120C18': '0D0F17',
    '12101E': '11141D',
    '13101E': '11141D',
    '140E1C': '0F121B',
    '161220': '13161F',
    '1A1225': '141723',
    '1A1229': '141827',
    '1E1530': '181C2D',
    '1E1628': '181B26',
    '1E1630': '191D2D',
    '1E1A2A': '1C1E28',
    '24123E': '181E38',
    '251A44': '1E2540',
    '2A1E40': '21273D',
    '2A2038': '222636',
    '2C2433': '262832',
    '2E1F52': '242C4D',
    '2E2050': '252C4B',
    '3A2560': '2B345A',
    '3A2F4D': '32374A',
    '3A3050': '33384D',
    '3D2A66': '731647',
    '3D2B5C': '303857',
    '3D2D5C': '323957',
    '4A3070': '7F194F',
    '4A3470': '811B51',
    '4A3570': '811C52',
    '4A3A5E': '414557',
    '4A3A6A': '474A5D',
    '4A3B66': '45495C',
    '4E3870': '831D53',
    '5530A8': 'B31A6C',
    '563E7C': '91205C',
    '5A3D8A': '9D2063',
    '5A4080': '95215F',
    '5B3F8A': '9E2164',
    '5C3D9C': 'AE206C',
    '6432C8': 'D21B7D',
    '6444B4': 'C8247B',
    '6B47A8': 'BE2577',
    '6C4F9B': 'B52974',
    '7A5BAE': 'CE2D83',
    '7B5FB5': 'D43289',
    '7C5FAB': 'CD2F84',
    '7D5BAD': 'CD2E83',
    '7D5CB0': 'D12D85',
    '7D5CB3': 'D32E86',
    '7D5CB8': 'D63189',
    '7D5DB5': 'D43088',
    '8060A8': '737995',
    '8261B8': 'D6358B',
    '8B6FBF': 'EF3F91',
    '8E6CC8': 'F04494',
    '8F2DAF': '19B3A8',
    '9070C0': 'F04092',
    '9890BC': '9BA1B1',
    '9A7BDE': 'F366A8',
    '9B7FD4': 'F261A5',
    '9C6FD6': 'F1549D',
    '9E8AB5': '949AAB',
    'A07DD0': 'F25BA1',
    'A098C4': 'A4A9B8',
    'A89BC2': 'A5AAB8',
    'A8A0C8': 'ABAFBD',
    'A8A0CC': 'ADB2BF',
    'B0A8CC': 'B2B6C2',
    'B57EDC': 'F367A8',
    'B8A8C8': 'AFB4C1',
    'B8B0C8': 'B4B8C4',
    'B8B0D4': 'CDB7C2',
    'BCB4D6': 'CFBBC5',
    'BFA6E4': 'D8B2C5',
    'C0B8DC': 'D5BFCA',
    'C8B4FF': 'EEC5DA',
    'C9A0F0': 'E0B0C8',
    'D2AAFF': 'ECBDD4',
    'D4C5E8': 'E1CCD6',
    'D8C7F0': 'E8CFDC',
    'DCC4F2': 'E9CDDB',
    'E4E0EC': 'EAE2E6',
    'E8D5FF': 'F6DEEA',
    'E8E0F0': 'EDE3E8',
    'E996FF': '71F4E7',
    'F0E8FF': 'FAEDF3',
    'F5F0FF': 'FCF3F8',
};

// Fantasy hex (no #) -> feudal Japan hex. Mirrors the [data-genre="japan"] block in theme/genre.css.
export const JAPAN_PALETTE = {
    '05040A': '080605',
    '080612': '0D0B09',
    '0A0812': '0F0C0A',
    '0D0A14': '110E0C',
    '0D0B1A': '15110E',
    '0F0C16': '13100D',
    '0F0D18': '15110E',
    '120C18': '14110E',
    '12101E': '1A1512',
    '13101E': '1A1512',
    '140E1C': '171310',
    '161220': '1C1714',
    '1A1225': '1F1916',
    '1A1229': '211B17',
    '1E1530': '2D1B18',
    '1E1628': '231C18',
    '1E1630': '2D1C19',
    '1E1A2A': '25221F',
    '24123E': '391C17',
    '251A44': '3F231F',
    '2A1E40': '3C2622',
    '2A2038': '302C28',
    '2C2433': '2E2C29',
    '2E1F52': '4C2A25',
    '2E2050': '4A2B26',
    '3A2560': '6F2416',
    '3A2F4D': '433E39',
    '3A3050': '46403A',
    '3D2A66': '75291B',
    '3D2B5C': '682A1F',
    '3D2D5C': '682C21',
    '4A3070': '802E20',
    '4A3470': '7F3225',
    '4A3570': '7F3426',
    '4A3A5E': '524C46',
    '4A3A6A': '5A524A',
    '4A3B66': '585049',
    '4E3870': '7E372A',
    '5530A8': 'B53923',
    '563E7C': '8B3C2F',
    '5A3D8A': '9D3B2A',
    '5A4080': '903E30',
    '5B3F8A': '9D3D2C',
    '5C3D9C': 'B43B25',
    '6432C8': 'D24128',
    '6444B4': 'D04128',
    '6B47A8': 'C0452F',
    '6C4F9B': 'AE4D3C',
    '7A5BAE': 'C35946',
    '7B5FB5': 'CB5D4A',
    '7C5FAB': 'BE5D4C',
    '7D5BAD': 'C25947',
    '7D5CB0': 'C55A47',
    '7D5CB3': 'C95A46',
    '7D5CB8': 'CF5A45',
    '7D5DB5': 'CB5B47',
    '8060A8': '918477',
    '8261B8': 'CE5F4B',
    '8B6FBF': 'D36D5B',
    '8E6CC8': 'DF6A55',
    '8F2DAF': 'B93A23',
    '9070C0': 'D46E5C',
    '9890BC': 'AEA69E',
    '9A7BDE': 'E38F76',
    '9B7FD4': 'D8917B',
    '9C6FD6': 'DB846A',
    '9E8AB5': 'A7A098',
    'A07DD0': 'D48E79',
    'A098C4': 'B6AEA6',
    'A89BC2': 'B5AEA8',
    'A8A0C8': 'BBB4AD',
    'A8A0CC': 'D7A495',
    'B0A8CC': 'C0BAB4',
    'B57EDC': 'E19179',
    'B8A8C8': 'BEB8B2',
    'B8B0C8': 'C0BCB8',
    'B8B0D4': 'C8C2BC',
    'BCB4D6': 'CBC5BF',
    'BFA6E4': 'E7B3A3',
    'C0B8DC': 'E2BDB2',
    'C8B4FF': 'F9C9BA',
    'C9A0F0': 'F4B19C',
    'D2AAFF': 'F9C1B0',
    'D4C5E8': 'EACCC3',
    'D8C7F0': 'E5DFD2',
    'DCC4F2': 'F4CEC2',
    'E4E0EC': 'E9E7E3',
    'E8D5FF': 'F0ECE4',
    'E8E0F0': 'ECE9E4',
    'E996FF': 'E2B85A',
    'F0E8FF': 'F7F5F0',
    'F5F0FF': 'FAF8F5',
};

// Fantasy hex (no #) -> cyberpunk hex. Mirrors the [data-genre="cyberpunk"] block in theme/genre.css;
// generated by scripts/generate-genre-palette.js cyberpunk - re-run it rather than editing by hand.
export const CYBERPUNK_PALETTE = {
    '05040A': '060607',
    '080612': '0A0B0D',
    '0A0812': '0B0C0E',
    '0D0A14': '0D0E10',
    '0D0B1A': '0F1114',
    '0F0C16': '0E1012',
    '0F0D18': '0F1114',
    '120C18': '0F1113',
    '12101E': '131518',
    '13101E': '131518',
    '140E1C': '121316',
    '161220': '15171B',
    '1A1225': '17191D',
    '1A1229': '191B1F',
    '1E1530': '1D2025',
    '1E1628': '1A1D21',
    '1E1630': '1D2025',
    '1E1A2A': '1C1F24',
    '24123E': '21252B',
    '251A44': '272B32',
    '2A1E40': '272B32',
    '2A2038': '25282F',
    '2C2433': '24282E',
    '2E1F52': '54141C',
    '2E2050': '53141C',
    '3A2560': '631721',
    '3A2F4D': '383C44',
    '3A3050': '3A3E46',
    '3D2A66': '6B1924',
    '3D2B5C': '3D424A',
    '3D2D5C': '3E434B',
    '4A3070': '771C28',
    '4A3470': '4A505A',
    '4A3570': '4A505B',
    '4A3A5E': '444A54',
    '4A3A6A': '4A505A',
    '4A3B66': '484E59',
    '4E3870': '4C525C',
    '5530A8': 'C90F28',
    '563E7C': '545B66',
    '5A3D8A': '5A616D',
    '5A4080': '565D6A',
    '5B3F8A': '5A626F',
    '5C3D9C': 'CA0F28',
    '6432C8': 'E9112E',
    '6444B4': 'E7112E',
    '6B47A8': 'DE112C',
    '6C4F9B': 'DA102B',
    '7A5BAE': 'EE1B37',
    '7B5FB5': 'EF2540',
    '7C5FAB': 'EE1C38',
    '7D5BAD': 'EE1A36',
    '7D5CB0': 'EE1E3A',
    '7D5CB3': 'EE213C',
    '7D5CB8': 'EF2540',
    '7D5DB5': 'EE243F',
    '8060A8': 'EE1A36',
    '8261B8': 'EF2A44',
    '8B6FBF': 'FFE204',
    '8E6CC8': 'FFE207',
    '8F2DAF': 'CD0F29',
    '9070C0': 'FFE205',
    '9890BC': '9BA3B1',
    '9A7BDE': 'FFE51E',
    '9B7FD4': 'FFE41A',
    '9C6FD6': 'FFE312',
    '9E8AB5': '949CAB',
    'A07DD0': 'FFE416',
    'A098C4': 'A4ABB8',
    'A89BC2': 'A5ACB8',
    'A8A0C8': 'ABB2BD',
    'A8A0CC': 'ADB4BF',
    'B0A8CC': 'B2B8C2',
    'B57EDC': 'FFE51E',
    'B8A8C8': 'AFB6C1',
    'B8B0C8': 'B4BAC4',
    'B8B0D4': 'BBC0C9',
    'BCB4D6': 'BEC3CC',
    'BFA6E4': 'FFE83B',
    'C0B8DC': 'C4C8D0',
    'C8B4FF': 'FBF3B8',
    'C9A0F0': 'FFE93F',
    'D2AAFF': 'FBF2AE',
    'D4C5E8': 'DCDAD1',
    'D8C7F0': 'FBF4BC',
    'DCC4F2': 'FBF4BB',
    'E4E0EC': 'EAE8E3',
    'E8D5FF': 'FDF8D7',
    'E8E0F0': 'EBEAE5',
    'E996FF': 'FF4D59',
    'F0E8FF': 'FEFBE9',
    'F5F0FF': 'FEFDF1',
};

// Fantasy hex (no #) -> primal hex. Mirrors the [data-genre="primal"] block in theme/genre.css;
// generated by scripts/generate-genre-palette.js primal - re-run it rather than editing by hand.
export const PRIMAL_PALETTE = {
    '05040A': '080706',
    '080612': '0D0B0A',
    '0A0812': '0E0C0A',
    '0D0A14': '110E0C',
    '0D0B1A': '14110F',
    '0F0C16': '13100E',
    '0F0D18': '14110F',
    '120C18': '14110E',
    '12101E': '191512',
    '13101E': '191512',
    '140E1C': '171411',
    '161220': '1C1714',
    '1A1225': '1E1A16',
    '1A1229': '211B18',
    '1E1530': '26201C',
    '1E1628': '221D19',
    '1E1630': '27211C',
    '1E1A2A': '25201B',
    '24123E': '2C2520',
    '251A44': '342C26',
    '2A1E40': '342C26',
    '2A2038': '302923',
    '2C2433': '302823',
    '2E1F52': '532318',
    '2E2050': '522318',
    '3A2560': '62291C',
    '3A2F4D': '473D35',
    '3A3050': '493F37',
    '3D2A66': '6A2D1F',
    '3D2B5C': '4D423A',
    '3D2D5C': '4E433B',
    '4A3070': '763222',
    '4A3470': '5D5047',
    '4A3570': '5E5147',
    '4A3A5E': '574B41',
    '4A3A6A': '5D5047',
    '4A3B66': '5C4F45',
    '4E3870': '605248',
    '5530A8': 'A44029',
    '563E7C': '6A5B50',
    '5A3D8A': '716256',
    '5A4080': '6D5E53',
    '5B3F8A': '736356',
    '5C3D9C': 'A54029',
    '6432C8': 'BE4A30',
    '6444B4': 'BC492F',
    '6B47A8': 'B6462D',
    '6C4F9B': 'B2452C',
    '7A5BAE': 'C94E32',
    '7B5FB5': 'CD5439',
    '7C5FAB': 'CA4E33',
    '7D5BAD': 'C94E32',
    '7D5CB0': 'CC4F33',
    '7D5CB3': 'CC5135',
    '7D5CB8': 'CD5439',
    '7D5DB5': 'CD5337',
    '8060A8': 'C94E32',
    '8261B8': 'CE573D',
    '8B6FBF': 'DC8E27',
    '8E6CC8': 'DC902B',
    '8F2DAF': 'A7412A',
    '9070C0': 'DC8E29',
    '9890BC': 'B2AA9A',
    '9A7BDE': 'E09B41',
    '9B7FD4': 'E0993E',
    '9C6FD6': 'DE9535',
    '9E8AB5': 'ADA492',
    'A07DD0': 'DF983A',
    'A098C4': 'B9B2A3',
    'A89BC2': 'BAB2A3',
    'A8A0C8': 'BFB8AA',
    'A8A0CC': 'C0B9AC',
    'B0A8CC': 'C4BDB0',
    'B57EDC': 'E09C42',
    'B8A8C8': 'C2BBAE',
    'B8B0C8': 'C5BFB3',
    'B8B0D4': 'CBC5B9',
    'BCB4D6': 'CDC8BD',
    'BFA6E4': 'E5AB5F',
    'C0B8DC': 'D1CCC3',
    'C8B4FF': 'F6DDBD',
    'C9A0F0': 'E6AD62',
    'D2AAFF': 'F4D9B5',
    'D4C5E8': 'E3DBCA',
    'D8C7F0': 'F6DFC1',
    'DCC4F2': 'F6DFC0',
    'E4E0EC': 'EEE9DF',
    'E8D5FF': 'FAECDA',
    'E8E0F0': 'EFEAE1',
    'E996FF': 'EA8053',
    'F0E8FF': 'FCF5EB',
    'F5F0FF': 'FDF8F2',
};

const PALETTES = {scifi: SCIFI_PALETTE, horror: HORROR_PALETTE, postapoc: POSTAPOC_PALETTE, pirate: PIRATE_PALETTE, urban: URBAN_PALETTE,
    japan: JAPAN_PALETTE, cyberpunk: CYBERPUNK_PALETTE, primal: PRIMAL_PALETTE};

const currentGenre = () =>
    (typeof document !== 'undefined' && document.documentElement.dataset.genre) || DEFAULT_GENRE;

/**
 * The real hex for a brand color in a genre, for code that cannot use a CSS variable.
 * Non-brand colors come back unchanged. Pass `genre` from useLightMode() in components so
 * they re-render on a switch; it defaults to whatever <html> says right now.
 */
export function themeHex(hex, genre = currentGenre()) {
    const palette = PALETTES[genre];
    if (!palette || typeof hex !== 'string') return hex;
    const key = hex.replace('#', '').toUpperCase();
    return palette[key] ? `#${palette[key]}` : hex;
}

/**
 * A color with a two-digit hex alpha, for both plain hex ('#AABBCC' -> '#AABBCC55', exactly
 * what the old string concatenation produced) and the theme variables the codemod left behind
 * ('var(--th-x)' -> 'rgba(var(--th-x-rgb), 0.333)').
 */
export function withAlpha(color, aa) {
    if (typeof color !== 'string') return color;
    // --th-x genre variables and their --lm-<role>-x light-mode twins (theme/lightMode.css),
    // which both carry -rgb companions.
    const varMatch = /^var\((--(?:lm-(?:tx|bg|bd)-)?(?:th-)?[0-9a-f]{6})\)$/.exec(color.trim());
    if (varMatch) return `rgba(var(${varMatch[1]}-rgb), ${+(parseInt(aa, 16) / 255).toFixed(3)})`;
    return color + aa;
}
